/**
 * Stale-while-revalidate cache for API payloads that are cheap to be briefly
 * stale about (the artefact catalog). Callers paint the cached value on the
 * first render and refresh from the network in the background, so a reload
 * shows real content instead of an empty grid.
 *
 * Entries record the build that wrote them. A deploy that changes a cached
 * type's shape therefore invalidates everything, rather than feeding last
 * week's shape into this week's components — at the cost of one cold load.
 */

const PREFIX = 'aeys.vco.cache.'

type Entry<T> = { v: string; d: T }

/** localStorage throws in Safari private mode and is absent in some test envs. */
function store(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

/**
 * The cached value, or null when there isn't a usable one — never written, bad
 * JSON, or written by an earlier build. A corrupt cache must never take a page
 * down, so every failure degrades to "no cache" and the caller fetches.
 */
export function readCache<T>(key: string): T | null {
  try {
    const raw = store()?.getItem(PREFIX + key)
    if (!raw) return null
    const entry = JSON.parse(raw) as Entry<T> | null
    if (!entry || entry.v !== __CACHE_VERSION__) return null
    return entry.d
  } catch {
    return null
  }
}

export function writeCache<T>(key: string, value: T): void {
  const entry: Entry<T> = { v: __CACHE_VERSION__, d: value }
  try {
    store()?.setItem(PREFIX + key, JSON.stringify(entry))
  } catch {
    // Quota exceeded or storage disabled. The cache is an optimisation; losing
    // it costs a skeleton on the next load, not correctness.
  }
}
